import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Flag, PhoneOff, Star, TrendingDown } from 'lucide-react';
import { callFlagOptions } from '../../data/mockContactCallInfo';
import type { CallFlag } from '../../data/mockContactCallInfo';
interface ContactCallFlagProps {
  value: CallFlag;
  onChange: (flag: CallFlag) => void;
}
export const flagStyles: Record<CallFlag, {
  className: string;
  icon: typeof Star;
}> = {
  'top-customer': {
    className: 'bg-[#10B981]/15 text-[#047857] border-[#10B981]/40',
    icon: Star
  },
  'dont-call': {
    className: 'bg-[#EF4444]/15 text-[#B91C1C] border-[#EF4444]/40',
    icon: PhoneOff
  },
  'not-worth-chasing': {
    className: 'bg-[#F59E0B]/15 text-[#B45309] border-[#F59E0B]/40',
    icon: TrendingDown
  },
  none: {
    className: 'bg-white text-[#64748B] border-dashed border-[#CBD5E1]',
    icon: Flag
  }
};

// Prominent call flag beside the contact name; click to change it
export function ContactCallFlag({
  value,
  onChange
}: ContactCallFlagProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);
  const style = flagStyles[value];
  const Icon = style.icon;
  const label = value === 'none' ? 'Add call flag' : callFlagOptions.find(option => option.value === value)?.label;
  return <div ref={containerRef} className="relative" data-id="element-1087" data-mp="2np8wx6y4rfob">
      <button type="button" onClick={() => setIsOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isOpen} aria-label={`Call flag: ${label}. Change`} className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-[13px] font-semibold transition-colors hover:brightness-95 ${style.className}`} data-id="element-1088" data-mp="2nofntzbp8e30">
        <Icon className="w-3.5 h-3.5" data-id="element-1089" data-mp="1udano7ikub6l" />
        {label}
        <ChevronDown className="w-3.5 h-3.5 opacity-70" data-id="element-1090" data-mp="1zbua09131ozh" />
      </button>
      {isOpen && <ul role="listbox" aria-label="Call flag" className="absolute left-0 top-full mt-1 z-30 w-[200px] bg-white border border-[#E2E8F0] rounded-md shadow-lg py-1" data-id="element-1091" data-mp="12ll5mad7hp3a">
          {callFlagOptions.map(option => {
        const OptionIcon = flagStyles[option.value].icon;
        const isSelected = option.value === value;
        return <li key={option.value} role="option" aria-selected={isSelected} data-id="element-1092" data-mp="1gdrnc0w0c44u">
                <button type="button" onClick={() => {
            onChange(option.value);
            setIsOpen(false);
          }} className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-[#0F172A] hover:bg-[#F8FAFC] text-left" data-id="element-1093" data-mp="ixvpj0emtd9w">
                  <OptionIcon className="w-3.5 h-3.5 text-[#64748B]" data-id="element-1094" data-mp="gc4puzryvxtw" />
                  <span className="flex-1" data-id="element-1095" data-mp="beuu72dzipxm">{option.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-1096" data-mp="6e720de0s31t" />}
                </button>
              </li>;
      })}
        </ul>}
    </div>;
}